import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { saveAs } from "file-saver";
import { Button } from "react-bootstrap";
import { IoSearchSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";
import { FaCalendarAlt } from "react-icons/fa";
import { formatDate } from "../utils/dateAndTimeConversion";
import { useGetAllReportingQuery } from "../store/api/school/ReportingSlice";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const initialDate = {
  startDate: "",
  endDate: "",
};

const ReportingAnalytics = () => {
  const { t } = useTranslation();

  const keyMapping = {
    bookings: t("reservations"),
    seatsUsed: t("seats_used"),
    occupancyRate: t("occupancy_rate"),
    revenue: t("revenue"),
  };

  const legendFormatter = (value) => keyMapping[value] || value;
  const tooltipFormatter = (value, name) => [value, keyMapping[name] || name];
  const { user } = useSelector((state) => state.UserReducer);
  const [date, setDate] = useState(initialDate);
  const [reporting, setReporting] = useState([]);

  // Get Reporting Analytics
  const { data, isSuccess } = useGetAllReportingQuery({
    startDate: date.startDate,
    endDate: date.endDate,
  });

  // Fetch Reporting Analytics Data
  useEffect(() => {
    if (isSuccess && data?.data) {
      setReporting(data?.data);
    }
  }, [data, isSuccess]);

  const handleDateChange = (selectedDate, field) => {
    setDate((prev) => {
      const formattedDate = selectedDate ? formatDate(selectedDate) : "";
      const newDates = { ...prev, [field]: formattedDate };

      if (
        field === "startDate" &&
        newDates.endDate &&
        formattedDate > newDates.endDate
      ) {
        newDates.endDate = "";
      }

      return newDates;
    });
  };

  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportToCSV = () => {
    const cols = ["date", "revenue", "bookings", "occupancyRate", "seatsUsed"];

    // Build header row using keyMapping
    const header = cols.map((c) =>
      c === "date" ? t("date") : keyMapping[c] || c,
    );

    // Build data rows
    const rows = reporting.map((d) =>
      cols.map((c) => {
        return d[c] ?? "";
      }),
    );

    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "analytics-report.csv");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        {/* Filters */}
        <div className="d-flex flex-wrap gap-2">
          <div className="d-flex flex-grow-1">
            <div className="position-relative w-100">
              <DatePicker
                className="form-control"
                selected={
                  date.startDate
                    ? new Date(moment(date.startDate, "YYYY-MM-DD").toDate())
                    : null
                }
                onChange={(selectedDate) =>
                  handleDateChange(selectedDate, "startDate")
                }
                selectsStart
                startDate={
                  date.startDate
                    ? new Date(moment(date.startDate, "YYYY-MM-DD").toDate())
                    : null
                }
                endDate={date.endDate ? new Date(date.endDate) : null}
                dateFormat="yyyy-MM-dd"
                placeholderText={`${t("select")} ${t("start")} ${t("date")}`}
              />
              <FaCalendarAlt className="calederIcon" />
            </div>
          </div>

          <div className="d-flex flex-grow-1">
            <div className="position-relative w-100">
              <DatePicker
                className="form-control"
                selected={
                  date.endDate
                    ? moment(date.endDate, "YYYY-MM-DD").toDate()
                    : null
                }
                onChange={(selectedDate) =>
                  handleDateChange(selectedDate, "endDate")
                }
                selectsEnd
                startDate={
                  date.startDate
                    ? moment(date.startDate, "YYYY-MM-DD").toDate()
                    : null
                }
                endDate={
                  date.endDate
                    ? moment(date.endDate, "YYYY-MM-DD").toDate()
                    : null
                }
                minDate={
                  date.startDate
                    ? moment(date.startDate, "YYYY-MM-DD").toDate()
                    : new Date()
                }
                dateFormat="yyyy-MM-dd"
                placeholderText={`${t("select")} ${t("end")} ${t("date")}`}
                disabled={!date.startDate}
              />
              <FaCalendarAlt className="calederIcon" />
            </div>
          </div>
        </div>

        <button
          className="btn btn-primary addDriver ms-auto"
          onClick={exportToCSV}
        >
          {t("btns.reset")}
        </button>
      </div>

      {/* Revenue Over Time — all roles */}
      <div className="bg-white shadow-md p-4 rounded mb-6">
        <h3 className="text-lg font-semibold mb-2">
          {t("revenue")} {t("over")} {t("time")}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={reporting}>
            <CartesianGrid stroke="#ccc" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => date.split("-")[2]}
            />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Line type="monotone" dataKey="revenue" stroke="#8884d8" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Reservations (Bookings) Bar Chart */}
      <div className="bg-white shadow-md p-4 rounded mb-6">
        <h3 className="text-lg font-semibold mb-2">
          {t("reservation")} {t("per")} {t("day")}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={reporting}>
            <CartesianGrid stroke="#ccc" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => date.split("-")[2]}
            />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="bookings" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Seat Usage Chart */}
      <div className="bg-white shadow-md p-4 rounded mb-6">
        <h3 className="text-lg font-semibold mb-2">
          {`${t("seat")} ${t("usage")} (${t("seats")} ${t("used")})`}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={reporting}>
            <CartesianGrid stroke="#ccc" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => date.split("-")[2]}
            />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="seatsUsed" fill="#ffc658" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Occupancy Line Chart */}
      <div className="bg-white shadow-md p-4 rounded">
        <h3 className="text-lg font-semibold mb-2">
          {t("occupancy")} {t("rate")} (%)
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={reporting}>
            <CartesianGrid stroke="#ccc" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => date.split("-")[2]}
            />
            <YAxis domain={[0, 100]} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Line type="monotone" dataKey="occupancyRate" stroke="#f56c6c" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default ReportingAnalytics;
